import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Database, Eye, FileText, Calendar, Shield, Tag, Loader2 } from 'lucide-react'
import Modal from './Modal'
import { api } from '../lib/api'

interface ViewCollectionModalProps {
  isOpen: boolean
  onClose: () => void
  collection: any
}

export default function ViewCollectionModal({ isOpen, onClose, collection }: ViewCollectionModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'objects'>('details')

  const { data: objects, isLoading: objectsLoading } = useQuery({
    queryKey: ['collection-objects', collection?.id],
    queryFn: () => collection ? api.getObjects(collection.id, { limit: 20 }) : null,
    enabled: isOpen && !!collection && activeTab === 'objects',
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000 // Keep in cache for 10 minutes
  })

  if (!collection) return null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const formatStixType = (type: string) => {
    return type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'indicator':
        return <Shield className="h-4 w-4 text-red-500" />
      case 'malware':
        return <Shield className="h-4 w-4 text-orange-500" />
      case 'vulnerability':
        return <Shield className="h-4 w-4 text-yellow-500" />
      default:
        return <FileText className="h-4 w-4 text-blue-500" />
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Collection: ${collection.title}`} size="xl">
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('objects')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'objects'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Objects ({objects?.objects?.length || 0})
            </button>
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'details' ? (
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Collection ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{collection.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Title</dt>
                  <dd className="mt-1 text-sm text-gray-900">{collection.title}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-1 text-sm text-gray-900">{collection.description || 'No description provided'}</dd>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <Eye className="h-5 w-5 mr-2 text-blue-500" />
                  <span className={`text-sm font-medium ${
                    collection.can_read ? 'text-green-700' : 'text-gray-500'
                  }`}>
                    {collection.can_read ? 'Read Enabled' : 'No Read Access'}
                  </span>
                </div>
                <div className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-green-500" />
                  <span className={`text-sm font-medium ${
                    collection.can_write ? 'text-green-700' : 'text-gray-500'
                  }`}>
                    {collection.can_write ? 'Write Enabled' : 'Read Only'}
                  </span>
                </div>
              </div>
            </div>

            {/* Media Types */}
            {collection.media_types && collection.media_types.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Supported Media Types</h3>
                <div className="flex flex-wrap gap-2">
                  {collection.media_types.map((mediaType: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {mediaType}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {objectsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <div className="text-center">
                    <p className="text-lg font-medium text-gray-900">Loading objects...</p>
                    <p className="text-sm text-gray-500">This may take a few seconds</p>
                  </div>
                </div>
              </div>
            ) : objects?.objects && objects.objects.length > 0 ? (
              <div className="space-y-3">
                {objects.objects.map((obj: any, index: number) => (
                  <div key={obj.id || index} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {getTypeIcon(obj.type)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-900">
                              {obj.name || obj.value || obj.pattern || formatStixType(obj.type)}
                            </h4>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {formatStixType(obj.type)}
                            </span>
                          </div>
                          {obj.description && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{obj.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>Modified: {formatDate(obj.modified)}</span>
                            </div>
                            {obj.labels && obj.labels.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <Tag className="h-3 w-3" />
                                <span>{obj.labels.slice(0, 2).join(', ')}</span>
                                {obj.labels.length > 2 && <span>+{obj.labels.length - 2} more</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {objects.more && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    More objects available... (showing first 20)
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Objects</h3>
                <p className="text-gray-500">
                  This collection doesn't contain any STIX objects yet.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}